import React from 'react';
import { CaseStudyShell } from '../components/case-study/CaseStudyShell';
import { CaseHeader } from '../components/case-study/CaseHeader';
import { CaseSection } from '../components/case-study/CaseSection';
import { Figure } from '../components/case-study/Figure';
import { NextCase } from '../components/case-study/NextCase';
import { Reveal } from '../components/Reveal';
import { KordisChatDemo } from '../components/kordis/KordisChatDemo';
import { PrototypeMock } from '../components/kordis/PrototypeMock';
import { DiscoveryBoard } from '../components/kordis/DiscoveryBoard';
import { TrustPatterns } from '../components/kordis/TrustPatterns';
import { kordisInteractionModel, kordisMeta } from '../data/kordis';
export function KordisCaseStudy() {
  return <CaseStudyShell data-id="element-786" data-mp="3rhsebyewvnb5">
      <CaseHeader overline="Case study · Financial services AI" title="Kordis: an AI CFO whose numbers you can check" intro="Kordis is a chat-first AI CFO. Fractional finance leaders ask questions in plain English and get answers back with their sources attached. As PM and lead designer, I designed the interaction model that makes those answers checkable: inline citations, confidence in plain words, and clarifying questions when a request is ambiguous." meta={kordisMeta} data-id="element-787" data-mp="j3s3avjgiq0i" />

      <Figure className="mt-14" caption="Every figure links back to where it came from. Hover or tap a citation to see its source." data-id="element-788" data-mp="1qphoa75jirzu">
        <KordisChatDemo data-id="element-789" data-mp="25dlysnufvx7j" />
      </Figure>

      <CaseSection index="01" label="The problem" title="Nobody could trace where the numbers came from" intro={<>
            <p data-id="element-791" data-mp="2ee0w6lkoh2re">
              Fractional CFOs juggle about a dozen clients at once, working out of spreadsheets and dashboards built for
              questions someone already thought to ask. A new question can take hours.
            </p>
            <p data-id="element-792" data-mp="f3iyabge4gzg">
              Even when an answer arrives, there's no trail back to where the numbers came from and no sign of how fresh
              the data is. Making it faster wouldn't fix that.
            </p>
          </>} data-id="element-790" data-mp="20gm04xzx5wqg" />

      <CaseSection index="02" label="Discovery" title="Finance leaders weren't afraid of AI. They didn't trust it." intro={<>
            <p data-id="element-794" data-mp="2t63137o74867">
              I started with questions for the team about how CFOs actually move between clients, then went where
              finance leaders talk candidly: Reddit communities, industry reports and AI tool round-ups.
            </p>
            <p data-id="element-795" data-mp="330v0mh1c953m">
              The complaints all pointed the same way. Tools hid their data, and automation broke down at data prep. The
              opportunity wasn't more automation. It was automation a CFO could check.
            </p>
          </>} data-id="element-793" data-mp="2k4vdpkn7wrqw">
        <DiscoveryBoard data-id="element-796" data-mp="3gw74pf1dw7x7" />
      </CaseSection>

      <CaseSection index="03" label="First prototype" title="Proving the concept with Kronos+" intro={<p data-id="element-798" data-mp="1a7ez0muik3ru">
            I turned the core features into a rough prototype: a copilot that spots a problem and lets the CFO model
            its impact live, the "can we afford two engineers?" question clients ask on calls.
          </p>} data-id="element-797" data-mp="3ltfp7ma08o1m">
        <Figure caption="My first prototype, Kronos+: a rough layout sketch, then a what-if scenario flow where the copilot spots a cost spike and models its impact on runway. Deliberately basic, built to prove the concept." data-id="element-799" data-mp="w6knhvc65moy">
          <PrototypeMock data-id="element-800" data-mp="1ouzzsa3x1qwl" />
        </Figure>
      </CaseSection>

      <CaseSection index="04" label="What I did" title="An interaction model for an agent you can question" data-id="element-801" data-mp="1oxm84bymsafv">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-id="element-802" data-mp="1f5gi37rxw33b">
          {kordisInteractionModel.map((item, i) => <Reveal key={item.title} delay={i * 0.05} className="h-full" data-id="element-803" data-mp="2cl2n6cwvy8ov" data-mpi={i}>
              <div className="h-full rounded-2xl bg-surface p-5" data-id="element-804" data-mp="27u42l14v6oft">
                <h3 className="text-sm font-semibold text-fg" data-id="element-805" data-mp="27st2bbamnytg">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-fg-muted" data-id="element-806" data-mp="1rdtovjx0e1nu">{item.body}</p>
              </div>
            </Reveal>)}
        </div>
        <p className="mt-6 max-w-2xl text-[15px] leading-relaxed text-fg-muted" data-id="element-807" data-mp="1ie6jkn15sbpi">
          I also defined the brand and a token-based component set, and I ran QA on the CFO chat.
        </p>
      </CaseSection>

      <CaseSection index="05" label="Designing for trust" title="Every number traces back to its source" intro={<>
            <p data-id="element-809" data-mp="2f6a2ni9pr7hc">Every answer shows the figures, where they came from and how current the data is.</p>
            <p data-id="element-810" data-mp="21aoh5l2ov5to">
              Confidence is stated in words, and when Kordis isn't sure, it asks. Finance leaders sign off on these
              numbers, so they need to know when an answer is shaky.
            </p>
          </>} data-id="element-808" data-mp="2b22f3oezrxkp">
        <TrustPatterns data-id="element-811" data-mp="2yset9h0tyqjq" />
      </CaseSection>

      <CaseSection index="06" label="Outcome" title="Shipped to engineering, heading to pilot" intro={<p data-id="element-813" data-mp="345n2fm4d70e2">
            The chat interaction model, the citation and trust patterns, and a token-based component set are in the
            hands of the engineering team. Kordis is in active build toward its first pilot.
          </p>} data-id="element-812" data-mp="2yu9hb47eeys1" />

      <NextCase title="Vendoo: an analytics dashboard resellers could do taxes with" href="/work/vendoo" data-id="element-814" data-mp="3cygt0fp9hm78" />
    </CaseStudyShell>;
}